<script setup lang="ts">
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import { TypeFieldList } from '@/entities/Inventory/TypeField'
import { useConfirm } from '@/shared/ui'
import { useHelpers, useTranslate } from '@/shared/lib'
import { computed, type PropType } from 'vue'
import './assets/style.scss'
import InventObjectTypesFieldCard from './InventObjectTypesFieldCard.vue'
import InventObjectTypesGeomForm from './InventObjectTypesGeomForm.vue'
import InventObjectTypesViewsForm from './InventObjectTypesViewsForm.vue'
import { InventObjectTypesEdges } from '@/entities/Inventory/ObjectTypesEdges'

const fields = defineModel<ObjectInventoryField[]>('fields', { required: true })

const props = defineProps({
  group: {
    type: Object as PropType<ObjectTypeGroup>,
    required: true,
  },
  isChild: {
    type: Boolean,
    default: false,
  },
  parentType: {
    type: Object as PropType<Record<string, unknown> | null>,
    default: null,
  },
  /** Корень с ровно одним ребёнком не может иметь собственных полей — кнопку прячем */
  hideAddField: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  editSettings: [field: ObjectInventoryField]
}>()

const { show } = useConfirm()
const { trs } = useTranslate()
const { generateKey } = useHelpers()

const parentFieldIds = computed(() => {
  const parentTypeFields = props.parentType?.fields as { id: string }[] | undefined
  return parentTypeFields?.map((item) => item.id) ?? []
})

const parentFields = computed(() => {
  const result = new Map<string, string | string[]>()
  const parentTypeFields = props.parentType?.fields as
    | { name: string; default_value: string | string[] }[]
    | undefined
  if (parentTypeFields) {
    parentTypeFields.forEach((item) => {
      result.set(item.name, item.default_value)
    })
  }
  return result
})

const addHandler = (isList: boolean) => {
  fields.value.push({
    id: generateKey(8),
    name: '',
    type: isList ? 'computed' : 'string',
    items: [],
    object_type: 0,
    default_value: '',
    multiple: false,
    count_object: false,
    is_filtered: false,
    is_table: false,
    is_system: false,
    is_global_search: false,
    group: props.group.id,
  })
}

const deleteHandler = (id: string) => {
  show({
    title: trs('itf.delete_field'),
    confirmFunc: () => {
      const idx = fields.value.findIndex((item) => item.id === id)
      if (idx !== -1) fields.value.splice(idx, 1)
    },
  })
}

const filedType = (groupType: string) => {
  const list: { type?: string; title: string; value?: string }[] = []

  TypeFieldList.forEach((item) => {
    if (groupType === 'LIST' && item.name !== 'Дополнительные') return

    list.push({ type: 'subheader', title: item.name })

    item.children.forEach((child) => {
      list.push({ title: child.name, value: child.key })
    })
  })

  return list
}
</script>

<template>
  <InventObjectTypesGeomForm v-if="group.name == 'Картографические настройки'" />

  <InventObjectTypesViewsForm v-else-if="group.name == 'Настройки поведения'" />

  <InventObjectTypesEdges v-else-if="group.type == 'PARENT'" />

  <template v-else>
    <div class="invent-object-types-fields__cards">
      <InventObjectTypesFieldCard
        v-for="item in fields.filter((field) => field.group === group.id)"
        :key="item.id"
        :field="item"
        :type-items="filedType(group.type)"
        :parent-default="parentFields.get(item.name)"
        :readonly="parentFieldIds.includes(item.id)"
        @edit-settings="emit('editSettings', $event)"
        @delete="deleteHandler"
      />
    </div>

    <VBtn
      v-if="!hideAddField"
      class="mt-4"
      color="default"
      variant="tonal"
      @click.prevent="addHandler(['LIST'].includes(group.type))"
    >
      {{ $t('itf.add_field') }}
    </VBtn>
  </template>
</template>
